import getConfig from 'next/config';

import {ProfileData} from '~/src/types/user';
import {CommentHistory, Comment} from '~/src/types/data/community';

import {getApiRequest, patchApiRequest} from '~/src/utilities/feed/api.js';
import {login, userIsLoggedIn} from '~/src/utilities/helpers/account';
import {refreshOrShouldLogin} from '~/src/utilities/helpers/login';
import {Logger} from '~/src/utilities/helpers/log';
import {utcToFormat} from '~/src/utilities/helpers/date';

const {publicRuntimeConfig: config} = getConfig();

interface GetUserProfileConfig {
    forceLogin?: boolean;
}

interface UserReactions {
    received: {
        [key: string]: number;
    };
    awarded: {
        [key: string]: number;
    };
}

const getUserProfileDefaultConfig = {
    forceLogin: true
};

export const getUserProfile = async (configOverrides?: GetUserProfileConfig): Promise<ProfileData | void> => {
    const {forceLogin} = {
        ...getUserProfileDefaultConfig,
        ...configOverrides
    };

    if (forceLogin) {
        const isLoggedIn = userIsLoggedIn();
        if (!isLoggedIn || (await refreshOrShouldLogin(isLoggedIn))) {
            return login();
        }
    }

    try {
        return await getApiRequest(config.user.service.profile);
    } catch (error) {
        Logger.info(`[${error.name}][Profile] Error while retrieving user profile`, {error});
        throw error;
    }
};

export const updateUserProfile = async (profile: ProfileData): Promise<any | void> => {
    const isLoggedIn = userIsLoggedIn();
    if (!isLoggedIn || (await refreshOrShouldLogin(isLoggedIn))) {
        return login();
    }

    try {
        await patchApiRequest(`${config.user.service.profile}?tenantId=${config.tenantId}`, {...profile, email: undefined});
    } catch (error) {
        if (error.statusCode === 422) {
            return await error.raw.json();
        }

        Logger.info(`[${error.name}][Profile] Error while updating user profile`, {error});
        throw error;
    }
};

export const getUserReactions = async (): Promise<UserReactions> => {
    try {
        return await getApiRequest(config.comment.service.userReactions);
    } catch (error) {
        if (error.statusCode === 422) {
            return await error.raw.json();
        }

        Logger.info(`[${error.name}][Profile] Error while fetching user reactions`, {error});
        throw error;
    }
};

export interface ActivityStatistics {
    visits: {
        articles: number;
        videos: number;
    };
    community: {
        shares?: number;
        comments?: number;
        ratings?: number;
    };
}

export const fetchActivityStatistics = async (): Promise<ActivityStatistics | undefined> => {
    try {
        return await getApiRequest(config.user.service.activityStatistics);
    } catch (error) {
        Logger.info(`[${error.name}][Profile] Error while fetching activity statistics`, {error});
        return;
    }
};

export interface HistoryItem {
    url: string;
    title: string;
    titleHeader: string;
    openInNewWindow: boolean;
    image: {
        variants: {
            big: {
                aspectRatio: number;
                focusPoint: {
                    x: number;
                    y: number;
                };
                height: number;
                src: string;
                width: number;
            };
            small: {
                aspectRatio: number;
                focusPoint: {
                    x: number;
                    y: number;
                };
                height: number;
                src: string;
                width: number;
            };
        };
    };
    type: string;
    lead: string;
    contentId: number;
    tenantId: number;
    sharesCount: number;
    commentingEnabled: boolean;
    commentsCount: number;
    contentRatingsCount: number;
    likesEnabled: boolean;
}
export interface ActivityHistory {
    nextLink: string;
    items: HistoryItem[];
}

export const fetchActivityHistory = async (limit = '6'): Promise<ActivityHistory | undefined> => {
    try {
        return await getApiRequest(`${config.user.service.activityHistory}?limit=${limit}&cursor=${Date.now()}`);
    } catch (error) {
        Logger.info(`[${error.name}][Profile] Error while fetching activity history`, {error});
        return;
    }
};

const formatDate = (comment: Comment) => (comment.createdAt = utcToFormat(comment.createdAt, 'DD.MM.YYYY, HH:mm'));

export const fetchCommentHistory = async (link?: string): Promise<CommentHistory | undefined> => {
    try {
        const url = link ? link : `${config.user.service.commentHistory}?tenantId=${config.tenantId}`;
        const data: CommentHistory = await getApiRequest(url);
        data.items.forEach((item) =>
            item.comments.forEach((comment) => {
                formatDate(comment);
                comment.replies.forEach(formatDate);
            })
        );
        return data;
    } catch (error) {
        Logger.info(`[${error.name}][Profile] Error while fetching comment history`, {error});
        return;
    }
};
